import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { personnelApi, AlertBadge, ALERT_STYLES } from './personnelApi';
import { useSortableTable, SortableTh, nameFromString } from '../SortableTable';

const CREDENTIAL_SORT_COLUMNS = {
    person: { type: 'name', value: (i) => nameFromString(i?.personName) },
    organization: { type: 'string', value: (i) => i?.organizationName },
    state: { type: 'string', value: (i) => i?.licenseState },
    expires: { type: 'date', value: (i) => i?.licenseExpiration },
    alert: { type: 'number', value: (i) => i?.daysUntilExpiration },
};

// Dashboard panel: credential expirations (Super Admin = all orgs, Org Manager = own orgs, server-scoped).
const CredentialAlertsPanel = () => {
  const { user } = useSelector((state) => state.auth);
  const eligible = user?.role === 'admin' || user?.role === 'adminStaff';
  const [data, setData] = useState(null);

  const tableSort = useSortableTable(data?.items, { id: 'personnel.credentialAlerts', columns: CREDENTIAL_SORT_COLUMNS, defaultSort: { key: 'expires', dir: 'asc' } });
  useEffect(() => {
    if (!eligible) return undefined;
    let alive = true;
    personnelApi
      .access()
      .then((a) => (a.mode ? personnelApi.alerts() : null))
      .then((r) => alive && setData(r))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [eligible]);

  if (!data) return null;
  return (
    <div className="shadow-sm p-3 mb-4 mr-4 ml-4 rounded-card bg-white">
      <div className="d-flex justify-content-between align-items-center">
        <h5 style={{ color: '#004aad', margin: 0 }}>
          <i className="fas fa-id-card"></i> Credential Expirations{' '}
          <span style={{ background: data.total ? '#dc2626' : '#6c757d', color: '#fff', borderRadius: 10, padding: '0 8px', fontSize: 13 }}>
            {data.total}
          </span>
        </h5>
        <Link to="/personnel" className="edit-pill">HR Personnel</Link>
      </div>
      <div className="d-flex flex-wrap mt-2" style={{ gap: 8 }}>
        {Object.keys(ALERT_STYLES).map((l) => (
          <span key={l} style={{ border: `1px solid ${ALERT_STYLES[l].bg}`, borderRadius: 6, padding: '2px 8px', fontSize: 13 }}>
            {ALERT_STYLES[l].label}: <b>{data.counts?.[l] || 0}</b>
          </span>
        ))}
      </div>
      {data.items.length === 0 ? (
        <small className="text-muted d-block mt-2">No licenses expire in the next 60 days.</small>
      ) : (
        <table className="table table-sm table-striped mt-2 mb-0">
          <thead>
            <tr>
              <SortableTh sortKey="person" sort={tableSort}>Person</SortableTh>
              <SortableTh sortKey="organization" sort={tableSort}>Organization</SortableTh>
              <SortableTh sortKey="state" sort={tableSort}>License state</SortableTh>
              <SortableTh sortKey="expires" sort={tableSort}>Expires</SortableTh>
              <SortableTh sortKey="alert" sort={tableSort}>Alert</SortableTh>
            </tr>
          </thead>
          <tbody>
            {tableSort.rows.slice(0, 10).map((i) => (
              <tr key={i.credentialId}>
                <td>
                  <Link to={`/personnel/${i.personKind}/${i.personId}`}>{i.personName}</Link>{' '}
                  <small className="text-muted">({i.roleLabel})</small>
                </td>
                <td>{i.organizationName}</td>
                <td>{i.licenseState || '-'}</td>
                <td>{i.licenseExpiration}</td>
                <td>
                  <AlertBadge level={i.alertLevel} days={i.daysUntilExpiration} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {data.items.length > 10 && <small className="text-muted">+{data.items.length - 10} more in HR Personnel</small>}
    </div>
  );
};

export default CredentialAlertsPanel;
